import { formatCurrency } from '@/utils/money';
import { isValidNumber } from '@/utils/number';

/**
 * Formats a numeric price string using the user's locale conventions.
 *
 * Only the integer portion is grouped; the decimal portion (if any) is
 * preserved exactly as the user typed it so partial input like "1234." or
 * "1234.5" is never mangled.
 *
 * Uses {@link Number.prototype.toLocaleString} with the en-US locale for
 * comma insertion instead of a hand-rolled regex.
 *
 * @param value - Raw numeric string (e.g. "1234.56", "1234", ".5", "")
 * @returns The locale-formatted string (e.g. "1,234.56" in en-US)
 *
 * @example
 * ```typescript
 * formatLocalePrice('1234.56');  // "1,234.56"
 * formatLocalePrice('1234567'); // "1,234,567"
 * formatLocalePrice('');        // ""
 * ```
 */
export const formatLocalePrice = (value: string): string => {
  if (!value) {
    return value;
  }

  const [integerPart, ...decimalParts] = value.split('.');
  const hasDecimal = value.includes('.');

  // format the integer portion with the en-US locale
  const localeIntegerPart =
    integerPart && !isNaN(Number(integerPart))
      ? Number(integerPart).toLocaleString('en-US')
      : integerPart;

  return hasDecimal
    ? `${localeIntegerPart}.${decimalParts.join('')}`
    : localeIntegerPart;
};

/**
 * Strips all commas from a string.
 *
 * Used to convert a comma-formatted display value back to a plain
 * numeric string before passing it to PriceEditor.updatePrice().
 *
 * @param value - Comma-formatted string (e.g. "1,234.56")
 * @returns Plain numeric string (e.g. "1234.56")
 *
 * @example
 * ```typescript
 * stripCommas('1,234.56');    // "1234.56"
 * stripCommas('1,234,567');   // "1234567"
 * stripCommas('123');         // "123"
 * ```
 */
export const stripCommas = (value: string): string => {
  return value.replace(/,/g, '');
};

/**
 * Matches a partially-typed positive decimal price: digits + optional commas
 * + at most one decimal point + optional fractional digits. Used to gate
 * keystrokes on price inputs so users can't type letters or extra dots.
 *
 * Allows the empty string so the user can clear the field.
 */
export const PRICE_INPUT_REGEX = /^[\d,]*\.?\d*$/;

/**
 * Formats a per-unit price in micros with its unit suffix for display.
 *
 * @param unitPriceInMicros Per-unit price in micros
 * @param currencyCode Currency code (e.g., "USD")
 * @param unitSuffix Abbreviated unit (e.g., "lb", "each")
 *
 * @returns Formatted unit price string, or empty string if price is invalid
 *
 * @example
 * ```typescript
 * formatUnitPriceDisplay(1412000, 'USD', 'lb');     // "$14.12/lb"
 * formatUnitPriceDisplay(1412000, 'USD');           // "$14.12"
 * formatUnitPriceDisplay(null, 'USD', 'lb');        // ""
 * ```
 */
export const formatUnitPriceDisplay = (
  unitPriceInMicros: number | null,
  currencyCode: string | undefined,
  unitSuffix: string | undefined,
): string => {
  if (!isValidNumber(unitPriceInMicros)) {
    return '';
  }
  const price = formatCurrency(unitPriceInMicros, currencyCode);
  return unitSuffix ? `${price}/${unitSuffix}` : price;
};

/**
 * Whether a secondary price (per-unit or total) should be displayed alongside
 * the primary price. When the pack contains only one unit, the unit and pack
 * prices are identical, so a secondary display would be redundant.
 *
 * @param unitCount Number of units in the pack — defaults to 1 when absent
 * @returns `true` when the pack has more than one unit, meaning the per-unit
 *   and total prices differ and both are worth showing
 *
 * @example
 * ```typescript
 * hasSecondaryPriceDisplay(10);        // true  — show "$1.42/lb" beside "$14.20"
 * hasSecondaryPriceDisplay(1);         // false — unit and pack price are the same
 * hasSecondaryPriceDisplay(null);      // false — treat missing as single-unit
 * hasSecondaryPriceDisplay(undefined); // false
 * ```
 */
export const hasSecondaryPriceDisplay = (
  unitCount: number | null | undefined,
): boolean => (unitCount ?? 1) > 1;
